import { createFileRoute, Link } from "@tanstack/react-router";
import { gatedRobotsMeta } from "@/lib/seo-gating";
import { HeartCrack, ArrowLeft, Wind, Activity, Users, Waves, Sparkles } from "lucide-react";
import { BookmarkButton } from "@/components/learn/BookmarkButton";
import { LearnCarousel, LEARN_THEMES, type LearnCard } from "@/components/learn/LearnCarousel";
import { SHAME_INTRO, SHAME_CARDS } from "@/data/learn/shame";

export const Route = createFileRoute("/learn/shame-based-trauma")({
  head: () => ({
    meta: [
      { title: "Shame-Based Trauma — Recovery Trauma™" },
      {
        name: "description",
        content:
          "How chronic shame develops, how it shapes self-worth, the nervous system and relationships, and what can support recovery. Trauma-informed education from Recovery Trauma™.",
      },
      { property: "og:title", content: "Shame-Based Trauma — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Something happened to you — it is not who you are. A compassionate guide to chronic shame, the inner critic, shame triggers and healthy guilt.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/shame-based-trauma" },
      { name: "twitter:card", content: "summary_large_image" },
      ...gatedRobotsMeta("/learn/shame-based-trauma"),
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/shame-based-trauma" }],
  }),
  component: ShamePage,
});

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5 text-start">
      {items.map((s) => (
        <li key={s} className="relative ps-5 before:absolute before:start-0 before:content-['•']">
          {s}
        </li>
      ))}
    </ul>
  );
}

const RELATED = [
  { to: "/breathing", label: "Breathing", icon: Wind },
  { to: "/grounding", label: "Grounding", icon: Activity },
  { to: "/learn/$topic", params: { topic: "ifs" }, label: "Parts work (IFS)", icon: Users },
  { to: "/learn/window-of-tolerance", label: "Window of Tolerance", icon: Waves },
  { to: "/learn/polyvagal-ladder", label: "Polyvagal Ladder", icon: Sparkles },
  { to: "/self-love", label: "Self-compassion", icon: Sparkles },
] as const;

function ShamePage() {
  const theme = LEARN_THEMES.peach;

  const cards: LearnCard[] = [
    {
      id: "intro",
      emoji: "💛",
      chip: "Start here",
      title: "Shame-Based Trauma",
      body: (
        <>
          <p>{SHAME_INTRO}</p>
          <p className="pt-1 text-center italic text-muted-foreground">
            Something painful happened to you. It is not what you are.
          </p>
        </>
      ),
    },
    ...SHAME_CARDS.map((c) => ({
      id: c.id,
      emoji: c.emoji,
      chip: c.chip,
      title: c.title,
      body: (
        <>
          {c.intro.map((p) => (
            <p key={p}>{p}</p>
          ))}

          {c.groups?.map((g) => (
            <div key={g.label} className="pt-1">
              <p className="mb-1.5 text-sm font-semibold text-foreground/80">{g.label}</p>
              <BulletList items={g.items} />
            </div>
          ))}

          {c.adulthood && <p className="pt-1">{c.adulthood}</p>}

          {c.reflection && (
            <div className="mt-2 rounded-xl border border-border/60 bg-white/60 p-4 text-start dark:bg-black/20">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                A gentle reflection
              </p>
              <p className="italic">{c.reflection}</p>
            </div>
          )}
        </>
      ),
    })),
    {
      id: "related",
      emoji: "🧭",
      chip: "Go on",
      title: "Resources that pair well with this",
      body: (
        <>
          <p className="text-center">
            Shame eases fastest when the body feels steady first. These sit alongside this guide.
          </p>
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            {RELATED.map(({ to, label, icon: Icon, ...rest }) => (
              <Link
                key={to + label}
                to={to}
                {...((rest as { params?: { topic: string } }).params
                  ? { params: (rest as { params: { topic: string } }).params }
                  : {})}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm text-foreground hover:bg-white dark:bg-black/30 dark:hover:bg-black/50"
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </Link>
            ))}
          </div>
        </>
      ),
    },
  ];

  return (
    <main className={`min-h-dvh px-4 py-10 ${theme.pageBg}`}>
      <div className="mx-auto max-w-3xl">
        <Link
          to="/learn"
          className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Learn
        </Link>

        <header className="mb-8 text-center">
          <div className={`mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${theme.chipBg} ${theme.chipText}`}>
            <HeartCrack className="h-3.5 w-3.5" /> Guide
          </div>
          <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
            Shame-Based Trauma
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
            How chronic shame develops, how it shapes self-perception, the nervous system
            and relationships — and where recovery can begin.
          </p>
          <div className="mt-4 flex justify-center">
            <BookmarkButton
              id="learn:shame-based-trauma"
              title="Shame-Based Trauma"
              href="/learn/shame-based-trauma"
              section="Learn"
            />
          </div>
        </header>

        <LearnCarousel
          cards={cards}
          theme={theme}
          storageKey="shame-based-trauma"
          showContents
          showCardNav
        />

        <p className="mt-10 rounded-2xl border border-border bg-muted/30 p-5 text-center text-xs leading-relaxed text-muted-foreground">
          Educational only — this is not a diagnosis or a substitute for therapy. If this
          brought something close to the surface, please reach out to{" "}
          <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">
            Crisis Resources
          </Link>
          .
        </p>
      </div>
    </main>
  );
}
